import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// components
import HeaderUserSettings from '../headers/user.settings';
import LayoutNavigationMenu from './navigation.menu';

// var
const {
  REACT_APP_DEFAULT_TITLE,
} = process.env;

export default function LayoutApplication(props) {

  // input
  const {
    children 
  } = props;

  // hooks
  const user = useSelector(context => context.user);

  // states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <>
      <Helmet>
        <title>{REACT_APP_DEFAULT_TITLE}</title>
      </Helmet>
      <div>
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-slate-800 px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <img
                    alt="neuko.io"
                    src="/swifix-logo.png"
                    className="h-8 w-auto"
                  />
                </div>
                <LayoutNavigationMenu />
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div
          className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:w-72 lg:flex-col ${
            sidebarCollapsed ? 'lg:hidden' : 'lg:flex'
          }`}
        >
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-slate-800 px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <img
                alt="neuko.io"
                src="/swifix-logo.png"
                className="py-4 h-[80px] w-auto"
              />
            </div>
            <LayoutNavigationMenu />
          </div>
        </div>

        <div
          className={`transition-all duration-300 ${
            sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-72'
          }`}
        >
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>

            <button
              type="button"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="-m-2.5 hidden p-2.5 text-gray-700 lg:block"
            >
              <span className="sr-only">
                {sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
              </span>
              <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>

            {/* Separator */}
            <div aria-hidden="true" className="h-6 w-px bg-gray-900/10 lg:hidden" />

            <div className="flex flex-1 justify-end gap-x-4 self-stretch lg:gap-x-6">

              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <BellIcon aria-hidden="true" className="h-6 w-6" />
                </button>
                

                {/* Separator */}
                <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" />

                {/* Profile dropdown */}
                <HeaderUserSettings name={user.name} />
              </div>
            </div>
          </div>

          <main className="py-[5vh] px-[2vw]">
            {children}
          </main>
        </div>
      </div>
    </>
  )
}
